/**
 * Commit markers (ADR-0036 decision 3, architecture §14.2): every view marks `qd:view-committed:<viewHash>` in a
 * layout effect when its rows commit, the issue page marks `qd:issue-committed:<id>`, and the shell marks
 * `qd:interactive` once (store ready + route committed + socket live, at the next frame). Budgets are measured to
 * these marks, never to "next paint" alone.
 */
import { useLayoutEffect } from 'react';

class Markers {
  private readonly at = new Map<string, number>();
  private interactiveDone = false;

  mark(name: string): void {
    let t = 0;
    try {
      t = globalThis.performance.mark(name).startTime;
    } catch {
      t = globalThis.performance?.now?.() ?? 0;
    }
    this.at.set(name, t);
    try {
      globalThis.dispatchEvent(new CustomEvent('qd:marker', { detail: { name, t } }));
    } catch {
      // no window (unit tests in node)
    }
  }

  /** `qd:interactive`, once per page load, on the frame after the conditions hold. */
  interactive(): void {
    if (this.interactiveDone) return;
    this.interactiveDone = true;
    const raf = globalThis.requestAnimationFrame ?? ((fn: FrameRequestCallback) => globalThis.setTimeout(() => fn(0), 0));
    raf(() => this.mark('qd:interactive'));
  }

  get isInteractive(): boolean {
    return this.interactiveDone;
  }

  snapshot(): Record<string, number> {
    return Object.fromEntries(this.at);
  }
}

export const markers = new Markers();

/** A view's commit marker: call with the view's definition hash and its result version. */
export function useViewCommitted(viewHash: string, version: unknown): void {
  useLayoutEffect(() => {
    markers.mark(`qd:view-committed:${viewHash}`);
  }, [viewHash, version]);
}

/** The issue page's commit marker. */
export function useIssueCommitted(issueId: string, version: unknown): void {
  useLayoutEffect(() => {
    markers.mark(`qd:issue-committed:${issueId}`);
  }, [issueId, version]);
}
